import React, { useEffect, useState } from 'react';
import { LuWallet } from "react-icons/lu";
import { IoEyeOutline, IoInformationCircleOutline } from "react-icons/io5";
import { FaRegEyeSlash } from "react-icons/fa6";
import { useDispatch, useSelector } from 'react-redux';
import { fetchWallets } from '../../actions/payments';
import { getAuthenticatedUser } from '../../actions/auth';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Send } from 'lucide-react';
import './balanceCard.css';

const conversionRates = [
    { currency_pair: "USD/NGN", fx_rate: 1542 },
    { currency_pair: "USD/GHS", fx_rate: 12.7 },
    { currency_pair: "USD/ZAR", fx_rate: 18.3 },
    { currency_pair: "USD/KES", fx_rate: 110.5 },
    { currency_pair: "USD/EGP", fx_rate: 30.9 },
    { currency_pair: "USD/MWK", fx_rate: 880.5 },
    { currency_pair: "USD/AOA", fx_rate: 515 },
    { currency_pair: "USD/MAD", fx_rate: 9.5 },
    // Add more as needed
];

const BalanceCard = ({ wallets }) => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    const { allowedCountries, currencyOptions, conversionRates } = useSelector((state) => state.country);
    const [showBalances, setShowBalances] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userCountryCode, setUserCountryCode] = useState(userDetails?.country_code || 'ng');
    const [userWallets, setUserWallets] = useState(wallets || []);  
    const dispatch = useDispatch();  

    const toggleBalanceVisibility = () => {
        setShowBalances((prev) => !prev); 
    };    

    useEffect(() => {getUserWallets(wallets);}, [wallets]);

    const fetchAllWallets = async () => {
        setLoading(true);
        try {
            const response = await fetchWallets();
            const wallets = response.data.wallets.wallets;

            if (!wallets || wallets.length === 0) return;

            // Enrich wallet data with currencyOptions
            const enrichedWallets = wallets.map((wallet) => {
                const match = currencyOptions.find(opt => opt.currencyCode === wallet.currency_code);
                return {
                    ...wallet,
                    currencyName: match?.currencyName,
                    countryCode: match?.countryCode,
                    currencySymbol: match?.symbol,
                    flag: match?.flagUrl,
                };
            });

            setUserWallets(enrichedWallets);
        } catch (error) {
            // console.error("Error fetching wallets:", error);
        } finally {
            setLoading(false);
        }
    };

    const walletsInfo = userDetails?.wallet;
    const getUserWallets = (walletsInfo) => {
        setLoading(true);
        const wallets = walletsInfo;

        if (!wallets || wallets.length === 0) return;

        const enrichedWallets = wallets.map((wallet) => {
            const match = currencyOptions.find(opt => opt.currencyCode === wallet.currency_code);
            return {
                ...wallet,
                currencyName: match?.currencyName,
                countryCode: match?.countryCode,
                currencySymbol: match?.symbol,
                flag: match?.flagUrl,
            };
        });
        

        setUserWallets(enrichedWallets);
        setLoading(false);
    }
    

    useEffect(() => {
        if (currencyOptions.length > 0) {
            fetchAllWallets();
        }
    }, [currencyOptions]);
    
    const calculateTotalBalance = () => {
        if (!userWallets.length) return "0.00";
    
        const rawAmount = userDetails?.total_balance?.amount || userDetails?.total_balance;
        const totalUSD = rawAmount ? (Number(String(rawAmount).replace(/[^0-9.-]+/g, "")) || 0) : 0;
    
        // Convert totalUSD to the user's local currency
        const userWallet = userWallets.find(w => w.countryCode?.toLowerCase() === userCountryCode.toLowerCase());
        const userCurrency = userWallet?.currencySymbol || '₦';
        const userCurrencyCode = userWallet?.currency;
    
        const userRateObj = conversionRates.find(rate => rate.currency_pair === `USD/${userCurrencyCode}`);
        const localTotal = totalUSD * (userRateObj?.fx_rate || 1);
    
        return `${userCurrency}${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(localTotal)}`;
    };
    

    const getFlagUrl = (countryCode) => {
        if (!countryCode) return "ng"; 
        return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;  
    };

    const getUserCurrencySymbol = (countryCode) => {
        const userWallet = userWallets.find(w => w.countryCode?.toLowerCase() === countryCode.toLowerCase());
        return userWallet?.currencySymbol || '₦';
    };

    const formatAmount = (amount) =>
        new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
    }).format(amount || 0);

    const cleanAndFormatAmount = (amount) => {
        if (amount === undefined || amount === null) return "0.00";
        const cleaned = Number(String(amount).replace(/[^0-9.-]+/g, ""));
        return isNaN(cleaned) ? "0.00" : formatAmount(cleaned);
    };

    return (
        <div className={`balance__cards ${userWallets?.length === 1 ? 'single-card' : 'has-peek'}`}>
            {loading ? (
                <div className="">
                    <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                </div>
            ) : (
                userWallets.map((wallet, index) => {
                    const themeClass = `card-` + (wallet.currency_code ? wallet.currency_code.toLowerCase() : 'default');
                    return (
                        <div key={index} className={`card premium-wallet-card ${themeClass} text-white`}>
                            <div className="glow-effect"></div>
                            
                            {/* Top Section Group */}
                            <div className="premium-wallet-card__content">
                                {/* Header row */}
                                <div className="d-flex gap-3 align-items-center">
                                    <img 
                                        src={wallet.flag ?? getFlagUrl(wallet.iso2_country_code)} 
                                        alt="" 
                                        width='38px' 
                                        height="38px" 
                                        className='rounded-circle flag-img' 
                                    />
                                    <h4 className="wrapper__card__right__title mb-0">
                                        {wallet.currency_code} {wallet.currencyName ? `(${wallet.currencyName})` : null}
                                    </h4>
                                </div>

                                {/* Balance Amount Row */}
                                <div className="mt-3 mb-0">
                                    {/* Available Balance Label */}
                                    <div className="">
                                        <p className="wrapper__card__balance__hint mb-0">
                                            Available balance
                                        </p>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h1 className="wrapper__card__right__amount mb-0">
                                            <span>{wallet.currencySymbol}{showBalances ? "******" : cleanAndFormatAmount(wallet.available_balance ?? wallet.balance)}</span>
                                        </h1>
                                        <button 
                                            onClick={toggleBalanceVisibility} 
                                            className="eye-toggle-btn"
                                            aria-label="Toggle balance visibility"
                                        >
                                            {showBalances ? <FaRegEyeSlash size={16} /> : <IoEyeOutline size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Ledger Balance Row */}
                                <div className="d-flex align-items-center">
                                    <div className="ledger-bal-capsule">
                                        <span>Main Bal.:</span>
                                        <strong>{wallet.currencySymbol}{showBalances ? "******" : cleanAndFormatAmount(wallet.balance)}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="wallet-card-actions">
                                {wallet.is_primary && (
                                    <Link to='/user/add_money' className="wallet_action_btn send-money">
                                        <Plus size={14} />
                                        <span>Add</span>
                                    </Link>
                                )}
                                <Link to='/user/converter' state={{ backToCalDefault: { sendCurrency: wallet.currency_code } }} className="wallet_action_btn convert-funds">
                                    <RefreshCw size={14} />
                                    <span>Convert</span>
                                </Link>
                                <Link to='/user/send_money' className="wallet_action_btn send-money">
                                    <Send size={14} />
                                    <span>Send</span> 
                                </Link>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default BalanceCard;